//@ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import ModalTemplate from 'PageTemplates/ModalTemplate';
import { Form } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
  createStorageItem,
  updateStorageItem,
} from 'services/apiRequests/storage.api';
import { STORAGE_ITEM_TYPES_ARRAY } from '../constants/storageTypes';
import ProducersSelect from './ProducersSelect';
import { setSpinner } from 'services/store/actions/view';
import { getGlobalSettings } from 'services/store/actions/settings';
import { useAlert } from 'react-alert';

// Schemat walidacji
const validationSchema = yup.object({
  type: yup
    .string()
    .oneOf(STORAGE_ITEM_TYPES_ARRAY, 'Nieprawidłowy typ produktu')
    .required('Typ jest wymagany'),
  name: yup
    .string()
    .required('Nazwa jest wymagana')
    .min(3, 'Minimalna długość nazwy to 3 znaki')
    .max(50, 'Maksymalna długość nazwy to 50 znaków'),
  price: yup
    .number()
    .typeError('Cena musi być liczbą')
    .min(0, 'Cena nie może być ujemna')
    .required('Cena jest wymagana'),
  minQuantity: yup
    .number()
    .integer('Ilość minimalna musi być liczbą całkowitą')
    .typeError('Ilość minimalna musi być liczbą')
    .min(0, 'Ilość minimalna nie może być ujemna')
    .required('Ilość minimalna jest wymagana'),
  desiredQuantity: yup
    .number()
    .integer('Pożądana ilość musi być liczbą całkowitą')
    .typeError('Pożądana ilość musi być liczbą')
    .min(0, 'Pożądana ilość nie może być ujemna')
    .required('Pożądana ilość jest wymagana'),
  producers: yup
    .array()
    .min(1, 'Wybierz co najmniej jednego producenta')
    .required('Producenci są wymagani'),
});

const StorageItemModal = ({ closeModal, onSuccess, item = null }) => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formikRef = useRef(null);
  const mdfProducers = useSelector(
    (state) => state.settings.mdfProducers || [],
  );
  const isEditing = !!item;

  useEffect(() => {
    dispatch(setSpinner(true));
    dispatch(
      getGlobalSettings(
        () => {
          dispatch(setSpinner(false));
        },
        () => {
          dispatch(setSpinner(false));
          alert.error('Błąd!');
        },
      ),
    );
  }, []);

  const initialValues = {
    type: item?.type || 'MDF',
    name: item?.name || '',
    price: item?.price || '',
    minQuantity: item?.minQuantity || 0,
    desiredQuantity: item?.desiredQuantity || 0,
    producers: [],
  };

  // Ustawienie producentów po załadowaniu danych
  useEffect(() => {
    if (item && mdfProducers.length > 0 && formikRef.current) {
      const selectedProducerIds =
        item.producers
          ?.map((p) => {
            const producer = mdfProducers.find((mp) => mp.name === p.name);
            return producer?._id;
          })
          .filter(Boolean) || [];

      formikRef.current.setFieldValue('producers', selectedProducerIds);
    }
  }, [item, mdfProducers]);

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    console.log(values);
    try {
      const productData = {
        ...values,
        price: parseFloat(values.price),
        minQuantity: parseInt(values.minQuantity, 10),
        desiredQuantity: parseInt(values.desiredQuantity, 10),
        producers: mdfProducers
          .filter((producer) => values.producers.includes(producer._id))
          .map((p) => ({ name: p.name })),
      };

      if (isEditing) {
        await updateStorageItem(item._id, productData);
        alert.success('Produkt został zaktualizowany pomyślnie!');
      } else {
        await createStorageItem(productData);
        alert.success('Produkt został dodany pomyślnie!');
      }

      if (onSuccess) {
        onSuccess();
      }
      closeModal();
    } catch (error) {
      console.error('Błąd podczas zapisywania produktu:', error);
      alert.error(
        `Błąd podczas ${
          isEditing ? 'aktualizacji' : 'dodawania'
        } produktu`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOk = () => {
    if (formikRef.current) {
      formikRef.current.submitForm();
    }
  };

  return (
    <ModalTemplate
      title={isEditing ? 'Edytuj produkt' : 'Nowy produkt'}
      closeModal={closeModal}
      onOk={handleOk}
      okButtonText={isEditing ? 'Zaktualizuj produkt' : 'Dodaj produkt'}
      disabled={isSubmitting}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        innerRef={formikRef}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
        }) => (
          <Form noValidate>
            <Form.Group className="mb-3">
              <Form.Label>Typ produktu *</Form.Label>
              <Form.Select
                name="type"
                value={values.type}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.type && !!errors.type}
              >
                {STORAGE_ITEM_TYPES_ARRAY.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.type}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nazwa produktu *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                placeholder="Wprowadź nazwę produktu"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.name && !!errors.name}
              />
              <Form.Control.Feedback type="invalid">
                {errors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Cena (PLN) *</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                name="price"
                placeholder="0.00"
                value={values.price}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.price && !!errors.price}
              />
              <Form.Control.Feedback type="invalid">
                {errors.price}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Ilość minimalna *</Form.Label>
              <Form.Control
                type="number"
                name="minQuantity"
                placeholder="0"
                value={values.minQuantity}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.minQuantity && !!errors.minQuantity}
              />
              <Form.Control.Feedback type="invalid">
                {errors.minQuantity}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Pożądana ilość *</Form.Label>
              <Form.Control
                type="number"
                name="desiredQuantity"
                placeholder="0"
                value={values.desiredQuantity}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={
                  touched.desiredQuantity && !!errors.desiredQuantity
                }
              />
              <Form.Control.Feedback type="invalid">
                {errors.desiredQuantity}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                Docelowa ilość elementu na magazynie
              </Form.Text>
            </Form.Group>

            <ProducersSelect
              producers={mdfProducers}
              selectedProducers={values.producers}
              onChange={(selectedProducers) =>
                setFieldValue('producers', selectedProducers)
              }
              error={
                touched.producers && errors.producers
                  ? errors.producers
                  : ''
              }
            />
          </Form>
        )}
      </Formik>
    </ModalTemplate>
  );
};

export default StorageItemModal;
